import { ArrowRight } from "@phosphor-icons/react";
import {
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";

import { useToast } from "@/client/hooks/use-toast";
import { adminApi, setAdminAuth } from "@/client/services/admin";

type FormValues = { email: string; password: string };

export const AdminLoginPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    try {
      const res = await adminApi.login(data.email, data.password);
      setAdminAuth(res.token, res.admin);
      const redirect = searchParams.get("redirect") ?? "/admin";
      void navigate(redirect, { replace: true });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Invalid email or password.";
      toast({
        variant: "error",
        title: "Login failed",
        description: message,
      });
      form.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Helmet>
        <title>Admin Login - VillageHut Resume</title>
      </Helmet>

      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div className="space-y-1.5">
              <h2 className="text-2xl font-semibold tracking-tight">Admin sign in</h2>
              <p className="text-sm text-muted-foreground">
                Sign in with your admin account to manage the site.
              </p>
            </div>

            <Form {...form}>
              <form className="flex flex-col gap-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                  name="email"
                  control={form.control}
                  rules={{ required: "Email is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder="admin@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="password"
                  control={form.control}
                  rules={{ required: "Password is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="current-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-center gap-x-2 pt-2">
                  <Button type="submit" disabled={loading} className="flex-1">
                    Sign in
                    <ArrowRight className="ml-2 size-4" />
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/">Back to site</Link>
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
